import React, { Suspense, lazy, useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { apiErrorMessage } from '../api/client';
import * as blogApi from '../api/blogApi';
import { ErrorBanner, Skeleton, Spinner } from '../components/ui';
import { IconArrowLeft, IconCheck } from '../components/icons';
import {
  BlogPost,
  BlogStatus,
  EXCERPT_MAX,
  TITLE_MAX,
  formatPublishedDate,
  slugify,
} from '../types/blog';

// TipTap and ProseMirror are ~400 KB, and this is the only page in the panel
// that uses them. Loaded on demand so that weight sits behind the blog editor
// rather than in the bundle every other page has to download first — the one
// split in this app, and it earns itself.
const RichTextEditor = lazy(() => import('../components/RichTextEditor'));

/**
 * The full-page post editor, for both /blog/new and /blog/:id/edit.
 *
 * One component for both because the two differ in exactly three places — the
 * initial fetch, the save verb, and which buttons the footer shows — and a
 * second copy would have to be kept in step with this one field for field.
 */

interface Form {
  title: string;
  slug: string;
  excerpt: string;
  cover_image: string;
  body: string;
  status: BlogStatus;
}

const EMPTY_FORM: Form = {
  title: '',
  slug: '',
  excerpt: '',
  cover_image: '',
  body: '',
  status: 'draft',
};

/**
 * TipTap represents an empty document as "<p></p>", not "".
 *
 * Every length check has to see through that, or a post with nothing typed in it
 * reads as 7 characters of content and the server's 100-character minimum
 * rejects a save the form said was fine.
 */
function bodyLength(html: string): number {
  return html.replace(/<[^>]*>/g, '').replace(/&nbsp;/g, ' ').trim().length;
}

type SlugCheck = 'idle' | 'checking' | 'available' | 'taken';

/* ---------- Cover image preview ---------- */

/**
 * A thumbnail of the cover, or a plain line of text when the URL doesn't load.
 *
 * Keyed on the URL by the parent so that changing it remounts this and clears a
 * previous failure — without that, one bad URL would leave "Image not found"
 * showing over a perfectly good replacement.
 */
function CoverPreview({ url }: { url: string }) {
  const [failed, setFailed] = useState(false);

  if (failed) {
    return (
      <p className="mt-2 rounded-xl border border-dashed border-edge px-3 py-6 text-center text-xs text-sub">
        Image not found
      </p>
    );
  }

  return (
    <img
      src={url}
      alt="Cover preview"
      onError={() => setFailed(true)}
      className="mt-2 max-h-40 w-full rounded-md object-cover"
    />
  );
}

/* ---------- Page ---------- */

export default function BlogEditorPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const isNew = !id;

  const [form, setForm] = useState<Form>(EMPTY_FORM);
  const [post, setPost] = useState<BlogPost | null>(null);
  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [dirty, setDirty] = useState(false);
  const [slugCheck, setSlugCheck] = useState<SlugCheck>('idle');

  /**
   * Has the author taken the slug over?
   *
   * Until they do, the slug tracks the title keystroke by keystroke. The moment
   * they edit the field themselves it stops following, because a URL they chose
   * must not be silently rewritten by a later title tweak. An existing post
   * starts locked: its slug is already published and is not the title's to
   * change.
   */
  const slugTouched = useRef(!isNew);

  // Load the post being edited.
  useEffect(() => {
    if (isNew) return;
    let alive = true;
    setLoading(true);
    blogApi
      .getPost(id!)
      .then((p) => {
        if (!alive) return;
        setPost(p);
        setForm({
          title: p.title,
          slug: p.slug,
          excerpt: p.excerpt,
          cover_image: p.cover_image,
          body: p.body,
          status: p.status,
        });
        setDirty(false);
      })
      .catch((err) => alive && setError(apiErrorMessage(err)))
      .finally(() => alive && setLoading(false));
    return () => {
      alive = false;
    };
  }, [id, isNew]);

  const update = useCallback(<K extends keyof Form>(key: K, value: Form[K]) => {
    setForm((f) => ({ ...f, [key]: value }));
    setDirty(true);
  }, []);

  function handleTitle(value: string) {
    setForm((f) => ({
      ...f,
      title: value,
      slug: slugTouched.current ? f.slug : slugify(value),
    }));
    setDirty(true);
    if (!slugTouched.current) setSlugCheck('idle');
  }

  function handleSlug(value: string) {
    slugTouched.current = true;
    // Normalised as it is typed, so the field can never show a URL the server
    // would rewrite on save.
    update('slug', slugify(value));
    setSlugCheck('idle');
  }

  async function checkSlug() {
    const slug = form.slug.trim();
    if (!slug) return setSlugCheck('idle');
    // An unchanged slug on an existing post is trivially available — skip the
    // request rather than ask the server about its own row.
    if (post && slug === post.slug) return setSlugCheck('available');
    setSlugCheck('checking');
    try {
      setSlugCheck((await blogApi.isSlugAvailable(slug, post?.id)) ? 'available' : 'taken');
    } catch {
      // Advisory only — the server rejects a real collision with a 409 on save,
      // so a failed check must not block the author or claim a false result.
      setSlugCheck('idle');
    }
  }

  /**
   * Save, optionally flipping the post live in the same request.
   *
   * `publishNow` is what separates the [Publish] button from [Save Draft]: both
   * write every field, and only the status differs.
   */
  async function save(publishNow: boolean) {
    const status: BlogStatus = publishNow ? 'published' : form.status;
    const payload = { ...form, status };

    setSaving(true);
    setError('');
    try {
      const saved = isNew
        ? await blogApi.createPost(payload)
        : await blogApi.updatePost(id!, payload);
      setPost(saved);
      setForm({
        title: saved.title,
        slug: saved.slug,
        excerpt: saved.excerpt,
        cover_image: saved.cover_image,
        body: saved.body,
        status: saved.status,
      });
      setDirty(false);
      slugTouched.current = true;
      // A new post has no id in the URL yet. Replacing rather than pushing keeps
      // Back going to the post list, not to a /blog/new that would start over.
      if (isNew) navigate(`/blog/${saved.id}/edit`, { replace: true });
    } catch (err) {
      setError(apiErrorMessage(err));
      // A 409 means the slug collided between the blur check and the save.
      if ((err as any)?.response?.status === 409) setSlugCheck('taken');
    } finally {
      setSaving(false);
    }
  }

  // Warn before a browser-level navigation away (reload, close, back to another
  // site). Route changes inside the panel are not covered — React Router v7
  // needs a data router for that, and this app uses <BrowserRouter>.
  useEffect(() => {
    if (!dirty) return;
    const onBeforeUnload = (e: BeforeUnloadEvent) => e.preventDefault();
    window.addEventListener('beforeunload', onBeforeUnload);
    return () => window.removeEventListener('beforeunload', onBeforeUnload);
  }, [dirty]);

  const excerptLeft = EXCERPT_MAX - form.excerpt.length;
  const bodyChars = bodyLength(form.body);
  const isPublished = form.status === 'published';
  const coverLooksValid = /^https?:\/\//i.test(form.cover_image.trim());

  // Mirrors the server's validation so Save is disabled rather than answering a
  // 400. The server still enforces all of it — this only saves a round trip.
  const canSave =
    !!form.title.trim() &&
    form.title.length <= TITLE_MAX &&
    !!form.excerpt.trim() &&
    form.excerpt.length <= EXCERPT_MAX &&
    coverLooksValid &&
    bodyChars >= 100 &&
    !!form.slug.trim() &&
    slugCheck !== 'taken';

  if (loading) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-8 w-64" />
        <div className="grid gap-6 lg:grid-cols-5">
          <div className="space-y-4 lg:col-span-3">
            <Skeleton className="h-12 w-full" />
            <Skeleton className="h-10 w-full" />
            <Skeleton className="h-24 w-full" />
            <Skeleton className="h-96 w-full" />
          </div>
          <div className="space-y-4 lg:col-span-2">
            <Skeleton className="h-40 w-full" />
            <Skeleton className="h-24 w-full" />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <button onClick={() => navigate('/blog')} className="btn-ghost !px-0 hover:!bg-transparent">
          <IconArrowLeft className="h-4 w-4" />
          Back to posts
        </button>
        {dirty && (
          <span className="inline-flex items-center gap-2 text-xs text-sub">
            <span className="h-1.5 w-1.5 rounded-full bg-warn" aria-hidden="true" />
            Unsaved changes
          </span>
        )}
      </div>

      {error && <ErrorBanner message={error} />}

      <div className="grid gap-6 lg:grid-cols-5">
        {/* ── Left column — the post itself ─────────────────────────────── */}
        <div className="space-y-5 lg:col-span-3">
          <div>
            <input
              value={form.title}
              onChange={(e) => handleTitle(e.target.value)}
              onBlur={checkSlug}
              placeholder="Post title..."
              maxLength={TITLE_MAX}
              // The one field with no label: at this size, in this position,
              // nothing else it could be.
              className="input !text-2xl !font-medium !py-3"
              aria-label="Post title"
            />
            {form.title.length > TITLE_MAX - 20 && (
              <p className="mt-1 text-right text-xs text-sub">
                {TITLE_MAX - form.title.length} characters left
              </p>
            )}
          </div>

          <div>
            <label htmlFor="blog-slug" className="label">
              URL: theperfumeculture.com/blog/
            </label>
            <div className="relative">
              <input
                id="blog-slug"
                value={form.slug}
                onChange={(e) => handleSlug(e.target.value)}
                onBlur={checkSlug}
                placeholder="auto-generated-from-title"
                className="input pr-24 font-mono text-sm"
              />
              <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs">
                {slugCheck === 'checking' && <Spinner className="h-3.5 w-3.5 text-sub" />}
                {slugCheck === 'available' && (
                  <span className="inline-flex items-center gap-1 text-ok">
                    <IconCheck className="h-3.5 w-3.5" />
                    Available
                  </span>
                )}
                {slugCheck === 'taken' && <span className="text-bad">Already taken</span>}
              </span>
            </div>
          </div>

          <div>
            <label htmlFor="blog-excerpt" className="label">
              Excerpt
            </label>
            <p className="-mt-1 mb-1.5 text-xs text-sub">
              Shown on the blog listing page. 2&ndash;3 sentences.
            </p>
            <textarea
              id="blog-excerpt"
              rows={3}
              value={form.excerpt}
              onChange={(e) => update('excerpt', e.target.value.slice(0, EXCERPT_MAX))}
              className="input resize-y"
            />
            <p
              className={`mt-1 text-right text-xs ${excerptLeft < 30 ? 'text-warn' : 'text-sub'}`}
            >
              {form.excerpt.length} / {EXCERPT_MAX}
            </p>
          </div>

          <div>
            <span className="label">Content</span>
            {/* The same footprint the editor reserves for itself while TipTap
                initialises, so the form doesn't jump when the chunk lands. */}
            <Suspense
              fallback={<div className="min-h-[456px] rounded-xl border border-edge bg-edge/10" />}
            >
              <RichTextEditor
                value={form.body}
                onChange={(html) => update('body', html)}
                placeholder="Write the post..."
              />
            </Suspense>
            {/* Only once there is something to be short — an untouched editor
                nagging "0 of 100" reads as an error before any mistake. */}
            {bodyChars > 0 && bodyChars < 100 && (
              <p className="mt-1.5 text-xs text-warn">
                {100 - bodyChars} more characters needed ({bodyChars} of 100 minimum)
              </p>
            )}
          </div>
        </div>

        {/* ── Right column — settings ───────────────────────────────────── */}
        <div className="space-y-5 lg:col-span-2">
          <div className="card p-5">
            <label htmlFor="blog-cover" className="label">
              Cover Image URL
            </label>
            <input
              id="blog-cover"
              value={form.cover_image}
              onChange={(e) => update('cover_image', e.target.value)}
              placeholder="https://..."
              className="input text-sm"
            />
            {/* Remounted per URL (key) so a previous load failure is cleared
                when the author pastes a different one. */}
            {coverLooksValid && (
              <CoverPreview key={form.cover_image.trim()} url={form.cover_image.trim()} />
            )}
            {!!form.cover_image.trim() && !coverLooksValid && (
              <p className="mt-2 text-xs text-warn">Must be a full URL starting with http.</p>
            )}
          </div>

          <div className="card space-y-5 p-5">
            <div>
              <span className="label">Status</span>
              <div className="flex gap-2">
                {(['draft', 'published'] as const).map((s) => (
                  <button
                    key={s}
                    type="button"
                    onClick={() => update('status', s)}
                    aria-pressed={form.status === s}
                    className={`flex-1 rounded-xl px-4 py-2 text-sm font-medium capitalize transition-colors ${
                      form.status === s
                        ? 'bg-gold text-[#1a1408]'
                        : 'border border-edge text-sub hover:bg-edge/40 hover:text-ink'
                    }`}
                  >
                    {s}
                  </button>
                ))}
              </div>
            </div>

            {/* Only a post that has actually gone live has a date to show, and
                it is read-only: it is stamped once by the server and never
                moves, however many times the post is edited afterwards. */}
            {post?.published_at && (
              <div>
                <span className="label">Published</span>
                <p className="text-sm">{formatPublishedDate(post.published_at)}</p>
              </div>
            )}

            <div className="space-y-2 border-t border-edge pt-4">
              {isPublished ? (
                <button
                  onClick={() => save(false)}
                  disabled={!canSave || saving}
                  className="btn-gold w-full"
                >
                  {saving && <Spinner />}
                  Update
                </button>
              ) : (
                <>
                  <button
                    onClick={() => save(true)}
                    disabled={!canSave || saving}
                    className="btn-gold w-full"
                  >
                    {saving && <Spinner />}
                    Publish
                  </button>
                  <button
                    onClick={() => save(false)}
                    disabled={!canSave || saving}
                    className="btn-outline w-full"
                  >
                    Save Draft
                  </button>
                </>
              )}
            </div>

            {!canSave && (
              <p className="text-xs text-sub">
                A title, excerpt, cover image URL and at least 100 characters of content are
                needed before this can be saved.
              </p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
